/**
 * Unified biometric / passkey quick-login status for native login + Profile.
 *
 * Two modes:
 *   - passkey       — works on the login screen after Sign Out (server passkey).
 *   - token_unlock  — refresh token + biometric gate; dedicated copy in Keystore
 *                     survives Sign Out; session refresh may be cleared on lock.
 */
import { loadStoredTokens } from '@/auth/tokenManager'
import { getLastLoginPhone } from './lastLoginPhone'
import {
  getTokenQuickLoginPhone,
  isTokenQuickLoginAvailable,
} from './biometric'
import {
  canUsePasskey,
  getPasskeyPhone,
} from './passkey'
import { getBiometricConfiguredPhone } from './biometricEnrollment'
import { isNative } from './platform'
import { enableTokenQuickLoginDetailed } from './biometric'

const BIO_TIMEOUT_MS = 6000

function withTimeout(promise, ms, fallback) {
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ])
}

/**
 * Best phone hint for passkey / biometric checks on the login screen.
 * @param {string} [explicitPhone]
 * @returns {Promise<string|null>}
 */
export async function resolveQuickLoginPhone(explicitPhone = null) {
  if (explicitPhone) return explicitPhone
  const lastPhone = getLastLoginPhone()
  const tokenPhone = await withTimeout(getTokenQuickLoginPhone(), BIO_TIMEOUT_MS, null)
  const passkeyPhone = await withTimeout(getPasskeyPhone(), BIO_TIMEOUT_MS, null)
  return lastPhone || tokenPhone || passkeyPhone || null
}

/**
 * @param {string} [phone]
 * @returns {Promise<{ active: boolean, mode: 'passkey'|'token_unlock'|'needs_reauth'|'none', phone: string|null }>}
 */
export async function getBiometricLoginStatus(phone = null) {
  if (!isNative()) {
    return { active: false, mode: 'none', phone: null }
  }

  await loadStoredTokens()
  const resolvedPhone = await resolveQuickLoginPhone(phone)
    || getBiometricConfiguredPhone()

  // Passkey mode only when THIS device has a local enrollment hint.
  // Server-only passkeys (other device) must not mark login as "active".
  if (resolvedPhone && canUsePasskey()) {
    const { isPasskeyLoginAvailable } = await import('./passkey')
    const localPasskeyReady = await withTimeout(
      isPasskeyLoginAvailable(resolvedPhone),
      BIO_TIMEOUT_MS,
      false,
    )
    if (localPasskeyReady) {
      return { active: true, mode: 'passkey', phone: resolvedPhone }
    }
  }

  const tokenReady = await withTimeout(isTokenQuickLoginAvailable(), BIO_TIMEOUT_MS, false)
  if (tokenReady) {
    const tokenPhone = await withTimeout(getTokenQuickLoginPhone(), BIO_TIMEOUT_MS, null)
    return {
      active: true,
      mode: 'token_unlock',
      phone: tokenPhone || resolvedPhone,
    }
  }

  const configuredPhone = getBiometricConfiguredPhone()
    || await withTimeout(getTokenQuickLoginPhone(), BIO_TIMEOUT_MS, null)
    || await withTimeout(getPasskeyPhone(), BIO_TIMEOUT_MS, null)
  if (configuredPhone) {
    const tokenReadyNow = await withTimeout(isTokenQuickLoginAvailable(), BIO_TIMEOUT_MS, false)
    if (tokenReadyNow) {
      return {
        active: true,
        mode: 'token_unlock',
        phone: configuredPhone,
      }
    }
    return {
      active: true,
      mode: 'needs_reauth',
      phone: configuredPhone,
    }
  }

  return { active: false, mode: 'none', phone: resolvedPhone }
}

/**
 * Whether the login screen should offer biometric / passkey quick sign-in.
 * @param {string} [phone]
 * @returns {Promise<boolean>}
 */
export async function isQuickLoginOffered(phone = null) {
  const status = await getBiometricLoginStatus(phone)
  return status.active
}

/**
 * Whether zombie-session cleanup should keep the refresh token (token_unlock mode).
 * @returns {Promise<boolean>}
 */
export async function shouldPreserveRefreshForBiometric() {
  if (!isNative()) return false
  if (getBiometricConfiguredPhone()) return true
  await loadStoredTokens()
  const tokenPhone = await withTimeout(getTokenQuickLoginPhone(), BIO_TIMEOUT_MS, null)
  if (tokenPhone) return true
  const { getBiometricRefreshToken } = await import('./biometric')
  const bioRefresh = await withTimeout(getBiometricRefreshToken(), BIO_TIMEOUT_MS, null)
  return !!bioRefresh
}

/** One-time: migrate Keystore phone hint → localStorage configured flag. */
export async function ensureBiometricConfiguredMigrated() {
  if (!isNative()) return
  if (getBiometricConfiguredPhone()) return
  const tokenPhone = await withTimeout(getTokenQuickLoginPhone(), BIO_TIMEOUT_MS, null)
  if (tokenPhone) {
    const { markBiometricLoginConfigured } = await import('./biometricEnrollment')
    markBiometricLoginConfigured(tokenPhone)
  }
}

/**
 * After password login, re-bind the refresh token to biometric unlock when the
 * user had previously enabled it on this device (e.g. after Sign Out).
 * @param {string} phone normalized E.164 phone
 * @returns {Promise<boolean>}
 */
export async function restoreTokenBiometricIfConfigured(phone) {
  if (!isNative() || !phone) return false
  const configured = getBiometricConfiguredPhone()
  if (!configured) return false
  const sameAccount = configured === phone
    || configured.replace(/\D/g, '') === phone.replace(/\D/g, '')
  if (!sameAccount) return false
  const result = await enableTokenQuickLoginDetailed(phone)
  return result.ok
}
